import React, { useState, useEffect, useMemo, useLayoutEffect, useRef, useCallback } from 'react'
import { Card, CardContent } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Modal } from 'components/ui/Modal'
import { useTranslation } from 'react-i18next'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'
import { hiringProcessApi, HiringStep, StepAction, Candidate as CandidateType, StepProgress, matchingJobsApi, Offer, MatchingJob } from 'lib/api'
import { useLocalizedHiringTemplateName } from 'hooks/useLocalizedHiringTemplateName'
import { getImageUrl } from 'lib/utils'
import { BulkSendEmailModal } from './BulkSendEmailModal'
import {
  Clock,
  Eye,
  User,
  CheckCircle,
  Target,
  FileText,
  BarChart3,
  Users,
  XCircle,
  CheckCircle2,
  Plus,
  UserPlus,
  Brain,
  TrendingUp,
  Gauge,
  Filter,
  Sparkles,
  Calendar,
  Edit,
  Mail,
  Send,
  Percent,
  Video,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useDroppable,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FigmaKanbanAddStepButton } from './figma/FigmaKanbanAddStepButton'
import { FigmaKanbanColumn } from './figma/FigmaKanbanColumn'
import { FigmaKanbanShortlistColumn } from './figma/FigmaKanbanShortlistColumn'
import { FigmaKanbanStepColumn } from './figma/FigmaKanbanStepColumn'
import { FigmaKanbanCandidateRow } from './figma/FigmaKanbanCandidateRow'

const IMMUTABLE_STEP_TYPES = ['initial', 'scanning']

const isStepImmutable = (stepType: string): boolean => IMMUTABLE_STEP_TYPES.includes(stepType)

/** User-created steps can be deleted; system/default and mandatory pipeline steps cannot. */
const isStepDeletable = (step: HiringStep): boolean =>
  step.is_deletable !== false && !step.is_mandatory

const getKanbanFigmaVariant = (stepType: string): 'candidates' | 'shortlist' | 'step' => {
  if (stepType === 'initial') return 'candidates'
  if (stepType === 'scanning') return 'shortlist'
  return 'step'
}

interface Candidate {
  id: number
  applicationId?: number
  name: string
  email: string
  role: string
  experience: string
  previousEmployer?: string
  location?: string
  appliedDate: string
  status: string
  matchScore: number
  aiDecision?: 'passed_by_ai' | 'failed_by_ai' | null
  avatar?: string
  commentsCount?: number
  attachmentsCount?: number
  actionsCount?: number
  lastActivity?: string
  seniorityLevel?: string
  interviewStatus?: string
  groupTags?: Array<{ name: string; color: string }>
  hasInterview?: boolean // Indicates if candidate has scheduled interview for current step
  currentStepInterview?: InterviewEvent // Interview for the current step
  allInterviews?: InterviewEvent[] // All interviews (including historical)
  interviewCount?: number // Total count of all interviews
  interviewScore?: number // AI evaluation global score for the interview
  interviewScoreData?: any // Full AI evaluation data for the interview
  hiddenDuplicates?: Array<{ id: number; name: string; role: string; matchScore: number; appliedDate: string }> // Hidden duplicates with scores
  passesFilters?: boolean // Whether the candidate passes the filter_matching criteria
  filterReasons?: string[] // Reasons why the candidate failed the filters
  scoreBreakdown?: Record<string, any> | null // Score breakdown from the matching results
  /** When set, candidate is in this hiring step (for offer-specific columns) */
  current_step_id?: number | null
}

interface KanbanColumn {
  id: string
  title: string
  color: string
  stepColor: string
  dotColor: string
  icon: React.ComponentType<any>
  candidates: Candidate[]
  stepType: string
  order: number
  stepId?: number // Add step ID to fetch actions
  /** True when step can be deleted (user-created / non-mandatory steps) */
  canDeleteStep?: boolean
}

interface InterviewEvent {
  candidateId?: number
  applicationId?: number
  start: Date | string
  end?: Date | string
  location?: string
  meetingLink?: string
  stepOrder?: number
  stepName?: string
  title?: string
}

interface ModernKanbanProps {
  candidates: Candidate[]
  onStatusChange: (applicationId: number, newStatus: string, stepId?: number | null) => void
  onCandidateClick: (candidateId: number) => void
  offerId?: number
  onAddCandidates?: () => void
  onManageCandidates?: () => void
  onLaunchAIMatching?: () => void
  minimumScore?: number
  onMinimumScoreChange?: (score: number) => void
  minimumInterviewScore?: number
  onMinimumInterviewScoreChange?: (score: number) => void
  hasMatchingRun?: boolean // If provided, use this value from the offer (from DB). Otherwise, calculate from candidates
  filterMatching?: Offer['filter_matching'] // Filter criteria from the offer
  skillWeights?: Offer['skill_weights'] // Weights for skill matching
  onScheduleInterview?: (candidate: Candidate, applicationId?: number) => void
  interviews?: InterviewEvent[] // List of scheduled interviews to show indicators
  customElementsBetweenColumns?: Array<{
    afterColumnOrder: number // Insert after column with this order
    element: React.ReactNode
  }>
  /** When this value changes, steps are refetched (e.g. after adding an offer-specific step) */
  stepsRefreshTrigger?: number
  /** Called when user deletes a deletable step from the offer kanban */
  onDeleteOfferStep?: (stepId: number) => void
  isLoadingMore?: Record<string, boolean>
  onLoadMore?: (columnId: string, stepId?: number, status?: string) => void
  hasMore?: Record<string, boolean>
  currentPage?: Record<string, number>
  onPageChange?: (columnId: string, page: number, stepId?: number, status?: string) => void
  /** Total candidate counts per column (from backend), independent of pagination */
  stageTotalCounts?: Record<string, number>
  pageSize?: number
  /** When true, drag-and-drop and other recruitment mutations are disabled (e.g. closed offer). */
  interactionsDisabled?: boolean
  /** Hide the small "Hiring process: …" line (e.g. when offer detail shows the Figma recruitment bar above) */
  hideHiringProcessLabel?: boolean
  /** Opens the add hiring step modal (circular + after the last kanban column). */
  onAddHiringStep?: () => void
  /** Fired when the offer hiring process template name is loaded. */
  onHiringProcessNameChange?: (name: string | null) => void
}

export function ModernKanban({
  candidates,
  onStatusChange,
  onCandidateClick,
  offerId,
  onAddCandidates,
  onManageCandidates,
  onLaunchAIMatching,
  minimumScore = 70,
  onMinimumScoreChange,
  minimumInterviewScore = 70,
  onMinimumInterviewScoreChange,
  hasMatchingRun: propHasMatchingRun,
  filterMatching,
  skillWeights,
  onScheduleInterview,
  interviews = [],
  customElementsBetweenColumns = [],
  stepsRefreshTrigger,
  onDeleteOfferStep,
  isLoadingMore = {},
  onLoadMore,
  hasMore = {},
  currentPage = {},
  onPageChange,
  stageTotalCounts = {},
  pageSize = 20,
  hideHiringProcessLabel = false,
  interactionsDisabled = false,
  onAddHiringStep,
  onHiringProcessNameChange,
}: ModernKanbanProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { user } = useAuth()
  const isSuperUser = (user as any)?.is_superuser || false
  const [hiringSteps, setHiringSteps] = useState<HiringStep[]>([])
  const [hiringProcessName, setHiringProcessName] = useState<string | null>(null)
  const localizedHiringProcessName = useLocalizedHiringTemplateName(hiringProcessName)
  const [loading, setLoading] = useState(true)
  const [activeId, setActiveId] = useState<string | number | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingMove, setPendingMove] = useState<{
    candidateId: number
    applicationId: number
    newStatus: string
    candidateName: string
    fromStep: string
    toStep: string
    targetStepId?: number
  } | null>(null)
  const [stepActions, setStepActions] = useState<Record<number, StepAction[]>>({})
  const [showSendEmailModal, setShowSendEmailModal] = useState(false)
  const [selectedStepForEmail, setSelectedStepForEmail] = useState<{ stepId: number; stepType: string; stepName: string } | null>(null)
  const [selectedCandidatesForEmail, setSelectedCandidatesForEmail] = useState<Candidate[]>([])
  const [matchingJob, setMatchingJob] = useState<MatchingJob | null>(null)

  // Use hasMatchingRun from offer (from DB) if provided, otherwise calculate from candidates
  // This is more reliable as it checks MatchingJob records and applications in the database
  const hasMatchingRun = useMemo(() => {
    if (propHasMatchingRun !== undefined) {
      return propHasMatchingRun
    }
    // Fallback: Check if matching has been run (indicated by candidates with matchScore > 0 or aiDecision)
    // This means matching was launched at least once, regardless of current candidate status
    return candidates.some(candidate =>
      (candidate.matchScore !== undefined && candidate.matchScore > 0) ||
      candidate.aiDecision !== undefined
    )
  }, [propHasMatchingRun, candidates])

  // Deduplication helper: Separate "applied" profiles, group others by email
  const dedupeCandidatesByEmail = (items: Candidate[]): Candidate[] => {
    const groups = new Map<string, Candidate[]>()
    const noEmail: Candidate[] = []

    // Group candidates by normalized email
    items.forEach(c => {
      const key = (c.email || '').trim().toLowerCase()
      if (!key) {
        noEmail.push(c)
        return
      }
      // If status is "applied" (Candidature initiale), keep separate by email|applied
      // Otherwise, group by email only (all advanced stages together)
      const compositeKey = c.status === 'applied' ? `${key}|applied` : key

      const arr = groups.get(compositeKey) || []
      arr.push(c)
      groups.set(compositeKey, arr)
    })

    const visible: Candidate[] = []

    // For each group, keep the one with the highest score and annotate hidden duplicates with their match scores
    groups.forEach((arr, compositeKey) => {
      const sorted = arr.slice().sort((a, b) => {
        // Sort by match score (highest first)
        const scoreA = a.matchScore || 0
        const scoreB = b.matchScore || 0
        if (scoreA !== scoreB) return scoreB - scoreA

        // If scores are equal, sort by most recent
        const ta = a.appliedDate ? new Date(a.appliedDate).getTime() : 0
        const tb = b.appliedDate ? new Date(b.appliedDate).getTime() : 0
        if (tb !== ta) return tb - ta

        // Otherwise sort by ID
        return (b.id || 0) - (a.id || 0)
      })
      const head = sorted[0]
      const hidden = sorted.slice(1)
      const annotated = {
        ...head,
        // Attach hidden duplicates metadata with match scores for UI rendering
        hiddenDuplicates: hidden.map(h => ({
          id: h.id,
          name: h.name,
          role: h.role,
          matchScore: h.matchScore,
          appliedDate: h.appliedDate
        })),
      } as Candidate & { hiddenDuplicates?: Array<{ id: number; name: string; role: string; matchScore: number; appliedDate: string }> }
      visible.push(annotated as unknown as Candidate)
    })

    // Include candidates without email unchanged
    return [...visible, ...noEmail]
  }

  // Apply deduplication to candidates
  const dedupedCandidates = useMemo(() => {
    return dedupeCandidatesByEmail(candidates)
  }, [candidates])

  // Helper function to generate filter reasons based on user role
  const getFilterReason = (filterType: 'experience' | 'hardSkills' | 'softSkills' | 'certifications' | 'languages' | 'education', detailedReason?: string): string => {
    if (isSuperUser && detailedReason) {
      return detailedReason
    }

    // Standard reasons for non-superusers
    const standardReasons: Record<string, string> = {
      'experience': t('aiMatching.filterReason.experience', 'Eliminated by years of experience'),
      'hardSkills': t('aiMatching.filterReason.hardSkills', 'Eliminated by required skills'),
      'softSkills': t('aiMatching.filterReason.softSkills', 'Eliminated by soft skills'),
      'certifications': t('aiMatching.filterReason.certifications', 'Eliminated by certifications'),
      'languages': t('aiMatching.filterReason.languages', 'Eliminated by languages'),
      'education': t('aiMatching.filterReason.education', 'Eliminated by education level'),
    }

    return standardReasons[filterType] || 'Eliminated by filter'
  }

  // Apply filter_matching logic to candidates (similar to AIMatchingModal)
  const candidatesWithFilterStatus = useMemo(() => {
    // If no filters or no matching job, return candidates as-is
    if (!filterMatching || !matchingJob) {
      return dedupedCandidates
    }

    // Parse filterMatching to determine active filters
    const hasActiveFilters = Boolean(
      (filterMatching.experience !== undefined && filterMatching.experience !== null && filterMatching.experience > 0) ||
      (filterMatching.hard_skills && Object.keys(filterMatching.hard_skills).length > 0) ||
      (filterMatching.soft_skills && Object.keys(filterMatching.soft_skills).length > 0) ||
      (filterMatching.certifications && Object.keys(filterMatching.certifications).length > 0) ||
      (filterMatching.languages && Object.keys(filterMatching.languages).length > 0) ||
      filterMatching.education_level
    )

    if (!hasActiveFilters) {
      return dedupedCandidates
    }

    // Get score breakdowns from matching results
    const scoreBreakdownMap = new Map<number, any>()
    matchingJob.results.forEach(result => {
      scoreBreakdownMap.set(result.candidate.id, result.score_breakdown)
    })

    // Apply filters to each candidate
    return dedupedCandidates.map(candidate => {
      const scoreBreakdown = scoreBreakdownMap.get(candidate.id)

      // Apply filters only to candidates in scanning status
      if (candidate.status !== 'scanning') {
        return candidate
      }

      // If no score breakdown, mark as not passing filters (can't verify)
      if (!scoreBreakdown) {
        return candidate
      }

      const filterReasons: string[] = []
      let candidatePassesFilters = true

      // Check experience filter
      if (filterMatching.experience !== undefined && filterMatching.experience !== null && filterMatching.experience > 0) {
        const experienceYears = scoreBreakdown?.experience_years?.details?.cv_experience_pertinente
        let years: number | null = experienceYears ?? null

        if (years === null) {
          const match = candidate.experience?.match(/(\d+(?:\.\d+)?)/)
          years = match ? Number(match[1]) : null
        }

        if (years === null || years < filterMatching.experience) {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Expérience: ${years ?? 0}/${filterMatching.experience} ans requis` : undefined
          filterReasons.push(getFilterReason('experience', detailedReason))
        }
      }

      // Check hard skills filter
      if (filterMatching.hard_skills && Object.keys(filterMatching.hard_skills).length > 0) {
        const details = scoreBreakdown?.hard_skills?.details
        const missingSkills: string[] = []

        if (details && Array.isArray(details)) {
          const similarityMap = new Map<string, number>()
          details.forEach((detail: any) => {
            if (detail.job_competence && detail.similarity !== undefined) {
              const weight = skillWeights?.hard_skills?.[detail.job_competence] || 1
              const normalizedSimilarity = Math.min(1, detail.similarity / weight)
              similarityMap.set(detail.job_competence.toLowerCase(), normalizedSimilarity)
            }
          })

          Object.entries(filterMatching.hard_skills).forEach(([skillName, requiredSimilarity]) => {
            const similarity = similarityMap.get(skillName.toLowerCase())
            const threshold = (requiredSimilarity as number) / 100 // Convert 0-100 to 0-1
            if (similarity === undefined || similarity < threshold) {
              missingSkills.push(`${skillName} (${Math.round((similarity ?? 0) * 100)}%/${requiredSimilarity}%)`)
            }
          })
        } else {
          Object.keys(filterMatching.hard_skills).forEach(skillName => {
            missingSkills.push(skillName)
          })
        }

        if (missingSkills.length > 0) {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Hard skills: ${missingSkills.join(', ')}` : undefined
          filterReasons.push(getFilterReason('hardSkills', detailedReason))
        }
      }

      // Check soft skills filter
      if (filterMatching.soft_skills && Object.keys(filterMatching.soft_skills).length > 0) {
        const details = scoreBreakdown?.soft_skills?.details
        const missingSkills: string[] = []

        if (details && Array.isArray(details)) {
          const similarityMap = new Map<string, number>()
          details.forEach((detail: any) => {
            if (detail.job_soft_skill && detail.similarity !== undefined) {
              const weight = skillWeights?.soft_skills?.[detail.job_soft_skill] || 1
              const normalizedSimilarity = Math.min(1, detail.similarity / weight)
              similarityMap.set(detail.job_soft_skill.toLowerCase(), normalizedSimilarity)
            }
          })

          Object.entries(filterMatching.soft_skills).forEach(([skillName, requiredSimilarity]) => {
            const similarity = similarityMap.get(skillName.toLowerCase())
            const threshold = (requiredSimilarity as number) / 100
            if (similarity === undefined || similarity < threshold) {
              missingSkills.push(`${skillName} (${Math.round((similarity ?? 0) * 100)}%/${requiredSimilarity}%)`)
            }
          })
        } else {
          Object.keys(filterMatching.soft_skills).forEach(skillName => {
            missingSkills.push(skillName)
          })
        }

        if (missingSkills.length > 0) {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Soft skills: ${missingSkills.join(', ')}` : undefined
          filterReasons.push(getFilterReason('softSkills', detailedReason))
        }
      }

      // Check certifications filter
      if (filterMatching.certifications && Object.keys(filterMatching.certifications).length > 0) {
        const details = scoreBreakdown?.certifications?.details
        const missingCerts: string[] = []

        if (details && Array.isArray(details)) {
          const similarityMap = new Map<string, number>()
          details.forEach((detail: any) => {
            if (detail.job_certification && detail.similarity !== undefined) {
              const normalizedSimilarity = Math.min(1, detail.similarity)
              similarityMap.set(detail.job_certification.toLowerCase(), normalizedSimilarity)
            }
          })

          Object.entries(filterMatching.certifications).forEach(([certName, requiredSimilarity]) => {
            const similarity = similarityMap.get(certName.toLowerCase())
            const threshold = (requiredSimilarity as number) / 100
            if (similarity === undefined || similarity < threshold) {
              missingCerts.push(`${certName} (${Math.round((similarity ?? 0) * 100)}%/${requiredSimilarity}%)`)
            }
          })
        } else {
          Object.keys(filterMatching.certifications).forEach(certName => {
            missingCerts.push(certName)
          })
        }

        if (missingCerts.length > 0) {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Certifications: ${missingCerts.join(', ')}` : undefined
          filterReasons.push(getFilterReason('certifications', detailedReason))
        }
      }

      // Check languages filter
      if (filterMatching.languages && Object.keys(filterMatching.languages).length > 0) {
        const details = scoreBreakdown?.languages?.details
        const missingLangs: string[] = []

        if (details && Array.isArray(details)) {
          const similarityMap = new Map<string, number>()
          details.forEach((detail: any) => {
            if (detail.job_langue && detail.job_niveau && detail.similarity_lang !== undefined) {
              const key = `${detail.job_langue} (${detail.job_niveau})`.toLowerCase()
              similarityMap.set(key, detail.similarity_lang)
            }
          })

          Object.entries(filterMatching.languages).forEach(([langName, requiredSimilarity]) => {
            const similarity = similarityMap.get(langName.toLowerCase())
            const threshold = (requiredSimilarity as number) / 100
            if (similarity === undefined || similarity < threshold) {
              missingLangs.push(`${langName} (${Math.round((similarity ?? 0) * 100)}%/${requiredSimilarity}%)`)
            }
          })
        } else {
          Object.keys(filterMatching.languages).forEach(langName => {
            missingLangs.push(langName)
          })
        }

        if (missingLangs.length > 0) {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Langues: ${missingLangs.join(', ')}` : undefined
          filterReasons.push(getFilterReason('languages', detailedReason))
        }
      }

      // Check education level filter
      if (filterMatching.education_level) {
        const details = scoreBreakdown?.niveau_etude?.details
        if (details && details.similarity !== undefined) {
          const normalizedSimilarity = Math.min(1, details.similarity)
          const threshold = filterMatching.education_level.similarity / 100
          if (normalizedSimilarity < threshold) {
            candidatePassesFilters = false
            const detailedReason = isSuperUser ? `Niveau d'étude: ${Math.round(normalizedSimilarity * 100)}%/${filterMatching.education_level.similarity}%` : undefined
            filterReasons.push(getFilterReason('education', detailedReason))
          }
        } else {
          candidatePassesFilters = false
          const detailedReason = isSuperUser ? `Niveau d'étude: non renseigné` : undefined
          filterReasons.push(getFilterReason('education', detailedReason))
        }
      }

      return {
        ...candidate,
        passesFilters: candidatePassesFilters,
        filterReasons,
        matchScore: candidatePassesFilters ? candidate.matchScore : 0,
        scoreBreakdown
      }
    })
  }, [dedupedCandidates, filterMatching, matchingJob])


  // Drag and drop sensors - add distance threshold to prevent accidental drags on button clicks
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Scroll position refs to prevent jump when data refreshes
  const boardScrollRef = useRef<HTMLDivElement | null>(null)
  const boardScrollPos = useRef(0)
  const columnScrollRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const columnScrollPos = useRef<Record<string, number>>({})

  // Load hiring steps on mount and when stepsRefreshTrigger or offerId changes (e.g. after adding step for offer)
  useEffect(() => {
    loadHiringSteps()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, stepsRefreshTrigger])

  useEffect(() => {
    onHiringProcessNameChange?.(hiringProcessName)
  }, [hiringProcessName, onHiringProcessNameChange])

  // Load matching job when offerId changes or when matching is run
  useEffect(() => {
    const loadMatchingJob = async () => {
      if (!offerId) return

      try {
        const response = await matchingJobsApi.getAll(undefined, { offer: offerId, status: 'completed' })
        const jobs = response.data.results || []
        if (jobs.length > 0) {
          // Get the most recent completed matching job
          const sortedJobs = jobs.sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )
          setMatchingJob(sortedJobs[0])
        } else {
          // Clear matching job if no jobs found
          setMatchingJob(null)
        }
      } catch (error) {
        console.error('Error loading matching job:', error)
      }
    }

    loadMatchingJob()
  }, [offerId, propHasMatchingRun, candidates.length, filterMatching]) // Reload when matching is run, candidates change, or filters change

  // Dynamic filtering: Move candidates from scanning to screening when they meet minimum score
  // DISABLED: Automatic movement is disabled to prevent filtered candidates from moving
  // Users must manually move candidates or use "Apply Results" in AI Matching modal
  /*
  useEffect(() => {
    if (!onStatusChange) return

    // Find candidates that need to be moved from scanning to screening
    // Use a timeout to ensure backend has updated the statuses before checking
    const timeoutId = setTimeout(() => {
      candidatesWithFilterStatus.forEach(candidate => {
        // Candidates without scores stay in scanning (current behavior)
        if (candidate.matchScore === undefined || candidate.matchScore === null || candidate.matchScore === 0) {
          return
        }

        // Don't move candidates who don't pass filters (they stay in scanning)
        if (candidate.passesFilters === false) {
          return
        }

        const score = candidate.matchScore
        const applicationId = candidate.applicationId ?? candidate.id

        // Only move from scanning to screening if they meet the threshold AND pass filters
        // NEVER move from screening back to scanning, even if score is below threshold
        if (candidate.status === 'scanning' && score >= minimumScore) {
          onStatusChange(applicationId, 'screening')
        }
        // Explicitly do NOT move candidates from screening to scanning
        // They stay in screening once they've been moved there
      })
    }, 600) // Wait a bit longer than the refreshApplications delay to ensure backend has updated

    return () => clearTimeout(timeoutId)
    // Trigger when minimumScore or candidates change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimumScore, candidatesWithFilterStatus])
  */

  const loadHiringSteps = async () => {
    try {
      setLoading(true)
      let steps: HiringStep[] = []
      if (offerId) {
        const response = await hiringProcessApi.getStepsForOffer(offerId)
        const data = response.data as { results?: HiringStep[] } | HiringStep[]
        steps = Array.isArray(data) ? data : (data?.results ?? [])
        const templateName = steps.find((s) => s.template_name)?.template_name ?? null
        setHiringProcessName(templateName)
      } else {
        const response = await hiringProcessApi.getDefaultTemplate()
        steps = response.data.steps ?? []
        setHiringProcessName(response.data.name ?? null)
      }
      if (steps.length > 0) {
        setHiringSteps(steps)

        // Load actions for all steps in parallel
        const actionEntries = await Promise.all(
          steps.map(async (step) => {
            try {
              const actionsResponse = await hiringProcessApi.getActions(step.id)
              const actions = Array.isArray(actionsResponse.data)
                ? actionsResponse.data
                : (actionsResponse.data as any)?.results || []

              const relevantActions = actions.filter(
                (a: StepAction) =>
                  a.is_active && (
                    (a.action_type === 'email' && a.trigger_type === 'manual') ||
                    (a.action_type === 'calendar')
                  )
              )
              return [step.id, relevantActions] as const
            } catch (error) {
              console.error(`Error loading actions for step ${step.id}:`, error)
              return [step.id, []] as const
            }
          })
        )
        setStepActions(Object.fromEntries(actionEntries))
      } else {
        setHiringSteps([])
        setHiringProcessName(null)
      }
    } catch (error) {
      console.error('Error loading hiring steps:', error)
      setHiringSteps([])
      setHiringProcessName(null)
    } finally {
      setLoading(false)
    }
  }

  // Get icon for step type
  const getStepIcon = (stepType: string) => {
    switch (stepType) {
      case 'initial': return Clock
      case 'scanning': return Brain
      case 'screening': return Eye
      case 'interview': return User
      case 'assessment': return BarChart3
      case 'offer': return CheckCircle
      case 'hired': return CheckCircle2
      case 'rejected': return XCircle
      case 'reference_check': return Users
      case 'onboarding': return Target
      default: return FileText
    }
  }

  // Convert hex color to Tailwind classes
  const getColorClasses = (hexColor: string, isLight: boolean = true) => {
    // This is a simplified mapping - in a real app you'd want a more comprehensive color system
    const colorMap: { [key: string]: { light: string; dark: string; dot: string } } = {
      '#3B82F6': { light: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800', dark: 'bg-blue-100 dark:bg-blue-900/20', dot: 'bg-blue-500' },
      '#10B981': { light: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800', dark: 'bg-green-100 dark:bg-green-900/20', dot: 'bg-green-500' },
      '#F59E0B': { light: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800', dark: 'bg-yellow-100 dark:bg-yellow-900/20', dot: 'bg-yellow-500' },
      '#EF4444': { light: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800', dark: 'bg-red-100 dark:bg-red-900/20', dot: 'bg-red-500' },
      '#8B5CF6': { light: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800', dark: 'bg-purple-100 dark:bg-purple-900/20', dot: 'bg-purple-500' },
      '#F97316': { light: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800', dark: 'bg-orange-100 dark:bg-orange-900/20', dot: 'bg-orange-500' },
    }

    return colorMap[hexColor] || colorMap['#3B82F6']
  }

  // Map application statuses to hiring step types
  const mapApplicationStatusToStepType = (applicationStatus: string): string => {
    const statusMapping: Record<string, string> = {
      'applied': 'initial',
      'scanning': 'scanning',
      'screening': 'screening',
      'interview': 'interview',
      'hr_interview': 'hr_interview',
      'offer': 'offer',
      'hired': 'hired',
      'rejected': 'rejected',
      'initial': 'initial',
    }
    return statusMapping[applicationStatus] || applicationStatus
  }

  // Map step type back to application status (for drag target)
  const stepTypeToApplicationStatus = (stepType: string): string => {
    const reverseMapping: Record<string, string> = {
      'initial': 'applied',
      'scanning': 'scanning',
      'screening': 'screening',
      'interview': 'interview',
      'hr_interview': 'hr_interview',
      'assessment': 'interview',
      'reference_check': 'interview',
      'offer': 'offer',
      'hired': 'hired',
      'rejected': 'rejected',
      'onboarding': 'hired',
      'withdrawn': 'rejected',
      'custom': 'interview',
    }
    return reverseMapping[stepType] || stepType
  }

  // Prefer the step's stored name (custom or default); fall back to category translation
  const getStepDisplayName = (stepType: string, name?: string): string => {
    const trimmed = (name || '').trim()
    if (trimmed) {
      return trimmed
    }
    const translationKey = `hiringProcess.stepTypes.${stepType}`
    const translated = t(translationKey)
    return translated !== translationKey ? translated : stepType
  }

  // Sort candidates by match score or interview score based on step type
  const isInterviewStepType = (stepType?: string) =>
    stepType === 'interview' || stepType === 'reboot_interview'

  const sortCandidatesByScore = (list: Candidate[], stepType?: string) =>
    [...list].sort((a, b) => {
      // For interview steps, prioritize interview score
      if (isInterviewStepType(stepType)) {
        const scoreA = a.interviewScore ?? 0
        const scoreB = b.interviewScore ?? 0
        if (scoreB !== scoreA) return scoreB - scoreA
      }

      // Secondary or default: sort by match score (highest first)
      const matchScoreA = a.matchScore ?? 0
      const matchScoreB = b.matchScore ?? 0
      if (matchScoreB !== matchScoreA) return matchScoreB - matchScoreA

      return 0
    })

  // Build status -> first step id (so candidates without current_step_id show in the right column by status)
  const sortedSteps = useMemo(() => hiringSteps.filter(s => s.is_active).sort((a, b) => a.order - b.order), [hiringSteps])
  const statusToStepId = useMemo(() => {
    const map: Record<string, number> = {}
    for (const step of sortedSteps) {
      const status = stepTypeToApplicationStatus(step.step_type)
      if (map[status] === undefined) map[status] = step.id
    }
    return map
  }, [sortedSteps])

  const getEffectiveStepId = (candidate: Candidate): number | undefined => {
    const statusStepId = statusToStepId[candidate.status]
    if (candidate.current_step_id != null) {
      const currentStep = sortedSteps.find((s) => s.id === candidate.current_step_id)
      const currentStepStatus = currentStep ? stepTypeToApplicationStatus(currentStep.step_type) : undefined

      if (
        statusStepId != null &&
        statusStepId !== candidate.current_step_id &&
        currentStepStatus !== candidate.status
      ) {
        return statusStepId
      }

      if (statusStepId != null && statusStepId !== candidate.current_step_id) {
        const currentOrder =
          currentStep?.order ?? 0
        const statusOrder = sortedSteps.find((s) => s.id === statusStepId)?.order ?? 0
        if (statusOrder > currentOrder) {
          return statusStepId
        }
      }
      return candidate.current_step_id
    }
    return statusStepId
  }

  // Auto-promote candidates from AI Interview to HR Interview when score meets threshold
  useEffect(() => {
    if (!onStatusChange) return

    const interviewStep = sortedSteps.find((s) => s.step_type === 'interview')
    const hrStep = sortedSteps.find((s) => s.step_type === 'hr_interview')
    if (!interviewStep || !hrStep) return

    const timeoutId = setTimeout(() => {
      candidatesWithFilterStatus.forEach((candidate) => {
        if (getEffectiveStepId(candidate) !== interviewStep.id) return
        if (candidate.interviewScore == null) return
        if (candidate.interviewScore < minimumInterviewScore) return

        const applicationId = candidate.applicationId ?? candidate.id
        onStatusChange(applicationId, 'hr_interview', hrStep.id)
      })
    }, 600)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimumInterviewScore, candidatesWithFilterStatus, sortedSteps, onStatusChange])

  // Create columns from hiring steps (unique id per step so offer-specific steps work for drag/drop)
  const columns: KanbanColumn[] = sortedSteps.map(step => {
    const Icon = getStepIcon(step.step_type)
    const colors = getColorClasses(step.color)
    return {
      id: `step-${step.id}`,
      title: getStepDisplayName(step.step_type, step.name),
      color: colors.light,
      stepColor: step.color || '#3B82F6',
      dotColor: colors.dot,
      icon: Icon,
      candidates: sortCandidatesByScore(
        candidatesWithFilterStatus.filter(c => getEffectiveStepId(c) === step.id),
        step.step_type
      ),
      stepType: step.step_type,
      order: step.order,
      stepId: step.id,
      canDeleteStep: isStepDeletable(step)
    }
  })

  // Update columns with candidates (recompute by effective step)
  const filteredColumns = columns.map(column => {
    const columnCandidates = sortCandidatesByScore(
      candidatesWithFilterStatus.filter(c => column.stepId != null && getEffectiveStepId(c) === column.stepId),
      column.stepType
    )
    return {
      ...column,
      candidates: columnCandidates
    }
  })

  // Cleanup scroll maps when steps disappear
  useEffect(() => {
    const ids = new Set(filteredColumns.map(col => col.id))
    Object.keys(columnScrollRefs.current).forEach(id => {
      if (!ids.has(id)) {
        delete columnScrollRefs.current[id]
        delete columnScrollPos.current[id]
      }
    })
  }, [filteredColumns])

  // Restore scroll positions after render to avoid jump
  useLayoutEffect(() => {
    if (boardScrollRef.current) {
      boardScrollRef.current.scrollLeft = boardScrollPos.current
    }

    filteredColumns.forEach(col => {
      const node = columnScrollRefs.current[col.id]
      if (node && columnScrollPos.current[col.id] !== undefined) {
        node.scrollTop = columnScrollPos.current[col.id]
      }
    })
  }, [filteredColumns, candidates])

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    if (interactionsDisabled) return
    setActiveId(event.active.id)
  }

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (interactionsDisabled) {
      return
    }

    if (!over) {
      return
    }

    const candidate = candidates.find(c => c.id === active.id)
    if (!candidate) {
      return
    }

    // Prevent drag-and-drop from "applied" stage - candidates can only move via matching
    if (candidate.status === 'applied') {
      return
    }

    // Get the source container (where the item was dragged from)
    const activeContainerId = active.data.current?.sortable?.containerId

    // Get the target container (where the item is being dropped)
    // Priority: 1. droppable data (column), 2. sortable containerId, 3. over.id
    let overContainerId: string | undefined

    // Check if dropped on a droppable column container
    if (over.data.current?.type === 'column' && over.data.current?.columnId) {
      overContainerId = over.data.current.columnId
    }
    // Check if dropped on a sortable item (candidate card)
    else if (over.data.current?.sortable?.containerId) {
      overContainerId = over.data.current.sortable.containerId
    }
    // Check if over.id matches a column id (fallback for empty columns)
    else {
      const overId = typeof over.id === 'string' ? over.id : String(over.id)
      // Check if this id matches any column id
      const matchingColumn = filteredColumns.find(col => col.id === overId)
      if (matchingColumn) {
        overContainerId = overId
      }
    }

    // If no target container or same container, do nothing
    if (!overContainerId || activeContainerId === overContainerId) {
      return
    }

    // Find source and target columns
    const sourceColumn = filteredColumns.find(col => col.id === activeContainerId)
    const targetColumn = filteredColumns.find(col => col.id === overContainerId)

    if (!targetColumn || !sourceColumn) {
      return
    }

    if (targetColumn.stepType === 'initial') {
      return
    }

    const newApplicationStatus = stepTypeToApplicationStatus(targetColumn.stepType)
    const currentEffectiveStepId = getEffectiveStepId(candidate)
    const targetStepId = targetColumn.stepId
    const isChangingStep = currentEffectiveStepId !== targetStepId

    // Prevent moving candidates from screening back to scanning
    if (candidate.status === 'screening' && newApplicationStatus === 'scanning') {
      return
    }

    // Check if moving backward (to step i-1)
    const isMovingBackward = targetColumn.order < sourceColumn.order

    // If moving backward and target column has candidates, prevent the move
    if (isMovingBackward && targetColumn.candidates.length > 0) {
      addToast({
        type: 'error',
        title: t('kanban.moveBlocked', 'Move Blocked'),
        message: t('kanban.cannotMoveBackWithCandidates', 'Cannot move candidate back to this step because it already contains candidates.'),
        duration: 4000
      })
      return
    }

    // Update when status changes OR when target column/step is different (e.g. moving to an added step with same status)
    if (candidate.status !== newApplicationStatus || isChangingStep) {
      const applicationId = candidate.applicationId ?? candidate.id

      // If moving forward, show confirmation modal
      const isMovingForward = targetColumn.order > sourceColumn.order
      if (isMovingForward) {
        setPendingMove({
          candidateId: candidate.id,
          applicationId,
          newStatus: newApplicationStatus,
          candidateName: candidate.name,
          fromStep: sourceColumn.title,
          toStep: targetColumn.title,
          targetStepId: targetColumn.stepId,
        })
        setShowConfirmModal(true)
        return
      }

      // If moving backward or same order, proceed without confirmation
      onStatusChange(applicationId, newApplicationStatus, targetColumn.stepId)
    }
  }

  // Handle confirmation modal confirm
  const handleConfirmMove = () => {
    if (pendingMove) {
      onStatusChange(pendingMove.applicationId, pendingMove.newStatus, pendingMove.targetStepId)
      setShowConfirmModal(false)
      setPendingMove(null)
    }
  }

  // Handle confirmation modal cancel
  const handleCancelMove = () => {
    setShowConfirmModal(false)
    setPendingMove(null)
  }

  // Handle opening send email modal
  const handleOpenSendEmailModal = (column: KanbanColumn) => {
    if (!column.stepId) return

    const step = hiringSteps.find(s => s.id === column.stepId)
    if (step) {
      setSelectedStepForEmail({
        stepId: column.stepId,
        stepType: column.stepType,
        stepName: column.title
      })
      setSelectedCandidatesForEmail(column.candidates)
      setShowSendEmailModal(true)
    }
  }

  // Sortable candidate card component
  const SortableCandidateCard = ({ candidate, containerId }: { candidate: Candidate; containerId: string }) => {
    // Disable drag from "applied" stage - candidates can only move via matching
    const dragDisabled = candidate.status === 'applied'
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: candidate.id,
      disabled: dragDisabled,
      data: {
        type: 'candidate',
        candidate,
        sortable: {
          containerId: containerId
        }
      }
    })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }

    // Find the step for this column (column id is step-{step.id})
    const stepIdFromCol = containerId.startsWith('step-') ? parseInt(containerId.replace('step-', ''), 10) : NaN
    const step = hiringSteps.find(s => s.id === stepIdFromCol) ?? hiringSteps.find(s => s.step_type === containerId)
    const stepOrder = step?.order || 0
    const passesFilters = candidate.passesFilters !== false
    const rawScore = Number(candidate.matchScore ?? 0)
    const hasMatchScore =
      candidate.matchScore !== undefined &&
      candidate.matchScore !== null &&
      rawScore >= 0
    const showScore =
      candidate.status !== 'applied' &&
      hasMatchScore &&
      (stepOrder >= 3 || (stepOrder === 2 && hasMatchingRun))
    const scorePassed =
      passesFilters &&
      (candidate.status === 'screening' ||
        candidate.aiDecision === 'passed_by_ai' ||
        rawScore >= minimumScore)

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...(!dragDisabled ? listeners : undefined)}>
        <FigmaKanbanCandidateRow
          name={candidate.name}
          role={candidate.role}
          avatar={candidate.avatar}
          showScore={showScore}
          score={passesFilters ? rawScore : 0}
          scorePassed={scorePassed}
          passesFilters={passesFilters}
          interviewScore={candidate.interviewScore}
          interviewScoreThreshold={minimumInterviewScore}
          onClick={() => onCandidateClick(candidate.id)}
        />
      </div>
    )
  }

  /** @deprecated Legacy rich card — kept for reference; all columns use FigmaKanbanCandidateRow. */
  const CandidateCard = ({
    candidate,
    onNameClick,
    dragDisabled,
    stepType,
    stepOrder,
    stepId,
    stepActions = [],
    onScheduleInterview,
  }: {
    candidate: Candidate
    onNameClick?: (candidateId: number) => void
    dragDisabled?: boolean
    stepType?: string
    stepOrder?: number
    stepId?: number
    stepActions?: StepAction[]
    onScheduleInterview?: (candidate: Candidate, applicationId?: number) => void
  }) => {
    const handleNamePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
      event.stopPropagation()
    }

    const handleNameClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      event.preventDefault()
      onNameClick?.(candidate.id)
    }

    // Check if this step has a calendar action with show_schedule_button enabled
    // The button visibility is controlled by the configuration field, not trigger type
    const hasScheduleButtonEnabled = stepActions.some(
      action =>
        action.action_type === 'calendar' &&
        action.is_active &&
        action.configuration?.show_schedule_button === true
    )
    const shouldShowScheduleButton = hasScheduleButtonEnabled && stepType !== 'rejected' && onScheduleInterview

    // Check if candidate passes filters (for scanning/screening steps)
    const passesFilters = candidate.passesFilters !== false
    const filterReasons = candidate.filterReasons || []

    return (
      <Card
        className={`group hover:shadow-md transition-all duration-200 border-0 shadow-sm relative ${dragDisabled ? 'cursor-default' : 'cursor-pointer'
          } ${!passesFilters
            ? 'bg-muted/50 dark:bg-muted/30 opacity-60 border-dashed'
            : 'bg-white dark:bg-gray-800'
          }`}
        aria-disabled={dragDisabled || undefined}
      >
        <CardContent className="p-3 overflow-visible">
          {/* Compact header with avatar, name, and match score */}
          <div className="flex items-center justify-between mb-2 gap-2">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <button
                type="button"
                onPointerDown={handleNamePointerDown}
                onClick={handleNameClick}
                className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xs overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                title={candidate.name}
              >
                {candidate.avatar ? (
                  <img
                    src={getImageUrl(candidate.avatar) || candidate.avatar}
                    alt={candidate.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  candidate.name.split(' ').map(n => n[0]).join('').toUpperCase()
                )}
              </button>
              <div className="flex-1 min-w-0 overflow-hidden">
                <button
                  type="button"
                  onPointerDown={handleNamePointerDown}
                  onClick={handleNameClick}
                  className={`font-medium text-sm text-left truncate hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary ${!passesFilters ? 'text-muted-foreground' : 'text-blue-600 dark:text-blue-300'
                    }`}
                  title={candidate.name}
                >
                  {candidate.name}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {candidate.role}
                </p>
                {candidate.interviewStatus && (
                  <div className="mt-1 flex items-center gap-1.5">
                    <Badge
                      variant={candidate.interviewStatus === 'completed' ? 'default' : 'outline'}
                      className={`text-[10px] px-1.5 py-0.5 capitalize flex items-center gap-1 ${candidate.interviewStatus === 'completed' ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20' : ''
                        }`}
                    >
                      {candidate.interviewStatus === 'completed' && <Video className="w-2.5 h-2.5" />}
                      {t(`interviewStatus.${candidate.interviewStatus}`, candidate.interviewStatus)}
                    </Badge>
                  </div>
                )}
                {/* Group tags */}
                {candidate.groupTags && candidate.groupTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {candidate.groupTags.map((groupTag, idx) => {
                      // Convert hex color to RGB for better contrast calculation
                      const hexToRgb = (hex: string) => {
                        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
                        return result ? {
                          r: parseInt(result[1], 16),
                          g: parseInt(result[2], 16),
                          b: parseInt(result[3], 16)
                        } : null
                      }

                      const rgb = hexToRgb(groupTag.color)
                      // Calculate luminance to determine if we should use light or dark text
                      const luminance = rgb ? (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255 : 0.5
                      const textColor = luminance > 0.5 ? '#000000' : '#ffffff'

                      return (
                        <Badge
                          key={idx}
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0.5 border-0"
                          style={{
                            backgroundColor: groupTag.color,
                            color: textColor
                          }}
                        >
                          {groupTag.name}
                        </Badge>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Match score and Préselection indicators and Duplicates */}
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {/* Match score - only show for non-applied candidates */}
              {candidate.status !== 'applied' && candidate.matchScore >= 0 && (
                <Badge
                  variant="outline"
                  className={`text-xs px-2 py-1 ${!passesFilters
                    ? 'bg-muted text-muted-foreground'
                    : (candidate.status === 'screening' || candidate.aiDecision === 'passed_by_ai' || (candidate.matchScore >= (minimumScore ?? 70)))
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : (candidate.aiDecision === 'failed_by_ai' || candidate.matchScore < (minimumScore ?? 70))
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        : 'bg-muted text-muted-foreground'
                    }`}
                >
                  {!passesFilters ? '0' : Math.floor(candidate.matchScore)}%
                </Badge>
              )}
              {/* Interview AI score - always show if score is available */}
              {candidate.interviewScore !== undefined && (
                <Badge
                  variant="outline"
                  className={`text-[10px] px-1.5 py-0.5 flex items-center gap-1 font-bold whitespace-nowrap ${candidate.interviewScore >= 70
                    ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800'
                    : candidate.interviewScore >= 40
                      ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                      : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
                    }`}
                >
                  <Sparkles className="w-3 h-3" />
                  AI: {Math.floor(candidate.interviewScore)}%
                </Badge>
              )}
              {/* Duplicate count badge - show if there are hidden duplicates */}
              {candidate.hiddenDuplicates && candidate.hiddenDuplicates.length > 0 && (
                <DuplicateBadgePopover candidate={candidate} t={t} minimumScore={minimumScore} />
              )}
              {/* Highlighting/AI result indicator */}
              {(candidate.status === 'screening' || candidate.status === 'scanning') &&
                candidate.matchScore >= 0 && passesFilters && (
                  (candidate.status === 'screening' || candidate.aiDecision === 'passed_by_ai' || candidate.matchScore >= (minimumScore ?? 70)) ? (
                    <div
                      className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                      title={t('kanban.passedByAI', 'Passed by AI')}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                  ) : (
                    <div
                      className="flex items-center justify-center w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                      title={t('kanban.failedByAI', 'Failed by AI')}
                    >
                      <XCircle className="w-4 h-4" />
                    </div>
                  )
                )}
            </div>
          </div>

          {/* Filter reasons - shown when candidate doesn't pass filters */}
          {!passesFilters && filterReasons.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {filterReasons.map((reason: string, idx: number) => (
                <Badge
                  key={`reason-${idx}`}
                  variant="outline"
                  className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800 text-xs"
                >
                  ⚠️ {reason}
                </Badge>
              ))}
            </div>
          )}

          {/* Experience description */}
          {candidate.experience && candidate.experience !== 'Not specified' && (
            <div className="mb-2">
              <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 break-words">
                {candidate.experience}
              </p>
            </div>
          )}

          {/* Minimal info row with interview indicator - only show if step has schedule action enabled */}
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 gap-2">
            <span className="truncate flex-1 min-w-0">{candidate.location || 'Remote'}</span>
            {shouldShowScheduleButton && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Show interview for current step */}
                {candidate.hasInterview && candidate.currentStepInterview && (
                  <div
                    className="flex items-center gap-1 text-blue-600 dark:text-blue-400 group relative"
                    title={(() => {
                      const interview = candidate.currentStepInterview!
                      const date = new Date(interview.start)
                      return `${t('kanban.interviewScheduled', 'Entretien planifié')}: ${date.toLocaleString()}`
                    })()}
                  >
                    <Calendar className="h-3.5 w-3.5" />
                  </div>
                )}
                {/* Show total interview count (including historical) */}
                {candidate.interviewCount && candidate.interviewCount > 0 && (
                  <div
                    className="flex items-center gap-1 text-muted-foreground group relative"
                    title={(() => {
                      const allInterviews = candidate.allInterviews || []
                      if (allInterviews.length === 0) return ''
                      const interviewList = allInterviews.map(int => {
                        const date = new Date(int.start)
                        const stepInfo = int.stepName ? ` (${int.stepName})` : ''
                        return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}${stepInfo}`
                      }).join('\n')
                      return `${t('kanban.allInterviews', 'Tous les entretiens')}:\n${interviewList}`
                    })()}
                  >
                    <Badge
                      variant="secondary"
                      className="text-[10px] px-1.5 py-0 h-4 font-medium"
                    >
                      {candidate.interviewCount}
                    </Badge>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Interview scheduling/editing button - show only if step has a calendar action, but not for rejected step */}
          {shouldShowScheduleButton && (
            <div
              className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700"
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                type="button"
                size="sm"
                variant={candidate.hasInterview ? "default" : "outline"}
                className={`w-full text-xs h-7 ${candidate.hasInterview ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                onPointerDown={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                onMouseDown={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                onPointerUp={(e) => {
                  e.stopPropagation()
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  onScheduleInterview(candidate, candidate.applicationId)
                }}
                title={
                  candidate.hasInterview
                    ? t('kanban.updateCurrentStepInterview', 'Modifier l\'entretien pour cette étape')
                    : candidate.interviewCount && candidate.interviewCount > 0
                      ? t('kanban.scheduleNewInterview', 'Planifier un nouvel entretien pour cette étape (historique: {count} entretien(s))', { count: candidate.interviewCount })
                      : t('kanban.scheduleInterview', 'Planifier un entretien')
                }
              >
                {candidate.hasInterview ? (
                  <>
                    <Edit className="h-3 w-3 mr-1" />
                    {t('kanban.updateInterview', 'Modifier l\'entretien')}
                  </>
                ) : (
                  <>
                    <Calendar className="h-3 w-3 mr-1" />
                    {t('kanban.scheduleInterview', 'Planifier un entretien')}
                  </>
                )}
              </Button>
            </div>
          )}

        </CardContent>
      </Card>
    )
  }

  interface KanbanColumnContainerProps {
    column: KanbanColumn
    children: React.ReactNode
    showManageButton: boolean
    onManageCandidates?: () => void
    minimumScore?: number
    onMinimumScoreChange?: (score: number) => void
    minimumInterviewScore?: number
    onMinimumInterviewScoreChange?: (score: number) => void
    allCandidates?: Candidate[]
    hasMatchingRun?: boolean
    stepActions?: Record<number, StepAction[]>
    onOpenSendEmailModal?: (column: KanbanColumn) => void
    onDeleteOfferStep?: (stepId: number) => void
    hasMore?: boolean
    onLoadMore?: () => void
    isLoadingMore?: boolean
    onLaunchAIMatching?: () => void
    onAddCandidates?: () => void
    columnCount?: number
    figmaVariant?: 'candidates' | 'shortlist' | 'step'
    stepColor?: string
    initialMatchingMode?: 'score' | 'top_k'
    topK?: number
  }

  const KanbanColumnContainer: React.FC<KanbanColumnContainerProps> = ({
    column,
    children,
    showManageButton,
    onManageCandidates,
    minimumScore,
    onMinimumScoreChange,
    minimumInterviewScore,
    onMinimumInterviewScoreChange,
    allCandidates = [],
    hasMatchingRun = false,
    stepActions = {},
    onOpenSendEmailModal,
    onDeleteOfferStep,
    hasMore,
    onLoadMore,
    isLoadingMore,
    onLaunchAIMatching,
    onAddCandidates,
    columnCount,
    figmaVariant = 'step',
    stepColor = '#3B82F6',
    initialMatchingMode,
    topK,
  }) => {
    const { t } = useTranslation()
    const { addToast } = useToast()
    const { setNodeRef, isOver } = useDroppable({
      id: column.id,
      data: {
        type: 'column',
        columnId: column.id
      }
    })
    const [showDeleteStepConfirmModal, setShowDeleteStepConfirmModal] = useState(false)

    const count = columnCount ?? column.candidates.length

    let figmaColumn: React.ReactNode
      if (figmaVariant === 'shortlist') {
        figmaColumn = (
          <FigmaKanbanShortlistColumn
            count={count}
            setDroppableRef={setNodeRef}
            isOver={isOver}
            minimumScore={minimumScore}
            onMinimumScoreChange={onMinimumScoreChange}
            initialMatchingMode={initialMatchingMode}
            topK={topK}
          >
            {children}
          </FigmaKanbanShortlistColumn>
        )
      } else if (figmaVariant === 'candidates') {
        figmaColumn = (
          <FigmaKanbanColumn
            count={count}
            setDroppableRef={setNodeRef}
            isOver={isOver}
            onAddCandidates={onAddCandidates}
            onLaunchAIMatching={onLaunchAIMatching}
            showAddButton={Boolean(onAddCandidates)}
          >
            {children}
          </FigmaKanbanColumn>
        )
      } else {
        const showInterviewScore =
          isInterviewStepType(column.stepType) && onMinimumInterviewScoreChange
        figmaColumn = (
          <FigmaKanbanStepColumn
            title={column.title}
            stepColor={stepColor}
            count={count}
            setDroppableRef={setNodeRef}
            isOver={isOver}
            canDeleteStep={column.canDeleteStep}
            onDeleteOfferStep={
              column.canDeleteStep && column.stepId && onDeleteOfferStep
                ? () => setShowDeleteStepConfirmModal(true)
                : undefined
            }
            minimumScore={showInterviewScore ? minimumInterviewScore : undefined}
            onMinimumScoreChange={showInterviewScore ? onMinimumInterviewScoreChange : undefined}
          >
            {children}
          </FigmaKanbanStepColumn>
        )
      }

      return (
        <>
          {figmaColumn}
          {showDeleteStepConfirmModal && column.canDeleteStep && column.stepId && onDeleteOfferStep && (
            <Modal
              isOpen={showDeleteStepConfirmModal}
              onClose={() => setShowDeleteStepConfirmModal(false)}
              title={t('hiringProcess.deleteStepTitle', 'Delete step')}
            >
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t('hiringProcess.confirmations.deleteStep', {
                    stepName: column.title,
                    defaultValue: `Are you sure you want to delete the step "${column.title}"?`,
                  })}
                </p>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setShowDeleteStepConfirmModal(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      onDeleteOfferStep(column.stepId!)
                      setShowDeleteStepConfirmModal(false)
                    }}
                  >
                    {t('hiringProcess.deleteStepCta', 'Delete step')}
                  </Button>
                </div>
              </div>
            </Modal>
          )}
        </>
      )
  }

  // Duplicate Badge Popover Component
  const DuplicateBadgePopover = ({ candidate, t, minimumScore }: { candidate: Candidate; t: any; minimumScore?: number }) => {
    const [showPopover, setShowPopover] = useState(false)
    const popoverRef = useRef<HTMLDivElement>(null)

    // Close popover when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
          setShowPopover(false)
        }
      }

      if (showPopover) {
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [showPopover])

    if (!candidate.hiddenDuplicates || candidate.hiddenDuplicates.length === 0) {
      return null
    }

    return (
      <div className="relative" ref={popoverRef}>
        <button
          onClick={(e) => {
            e.stopPropagation()
            setShowPopover(!showPopover)
          }}
          className="inline-flex items-center gap-1 rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 text-xs font-semibold hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
          title={t('kanban.hiddenDuplicates', 'Hidden duplicate profiles')}
        >
          <Users className="h-3 w-3" />
          +{candidate.hiddenDuplicates.length}
        </button>

        {showPopover && (
          <div
            className="absolute right-0 top-full mt-1 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-900 dark:text-white mb-2">
                {candidate.hiddenDuplicates.length} {t('kanban.otherProfiles', 'other profiles')}
              </p>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {candidate.hiddenDuplicates.map((dup, idx) => (
                  <div
                    key={dup.id}
                    className="text-[11px] p-1.5 bg-gray-50 dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{dup.name}</p>
                        <p className="text-gray-500 dark:text-gray-400 truncate">{dup.role}</p>
                        <p className="text-gray-400 dark:text-gray-500 text-[10px]">
                          {new Date(dup.appliedDate).toLocaleDateString()}
                        </p>
                      </div>
                      {dup.matchScore > 0 && (
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1.5 py-0.5 flex-shrink-0 ${dup.matchScore >= (minimumScore ?? 70)
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            }`}
                        >
                          {Math.floor(dup.matchScore)}%
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Infinite scroll sentinel component for columns
  const InfiniteScrollSentinel = ({ onLoadMore, hasMore, isLoadingMore }: { onLoadMore?: () => void, hasMore?: boolean, isLoadingMore?: boolean }) => {
    const sentinelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (!sentinelRef.current || !onLoadMore || !hasMore || isLoadingMore) return

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            onLoadMore()
          }
        },
        { threshold: 0.1, rootMargin: '250px' } // Increased margin for smoother loading
      )

      observer.observe(sentinelRef.current)
      return () => observer.disconnect()
    }, [onLoadMore, hasMore, isLoadingMore])

    if (!hasMore) return null

    return (
      <div ref={sentinelRef} className="h-10 w-full flex items-center justify-center mt-2">
        {isLoadingMore && (
          <div className="flex items-center space-x-2 bg-background/50 backdrop-blur-sm px-3 py-1.5 rounded-full border shadow-sm">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="text-xs font-medium text-muted-foreground">{t('common.loading', 'Loading...')}</span>
          </div>
        )}
      </div>
    )
  }

  // Column footer with pagination
  const ColumnPagination = ({
    columnId,
    stepId,
    status,
    page = 1,
    hasMore,
    isLoading
  }: {
    columnId: string,
    stepId?: number,
    status?: string,
    page?: number,
    hasMore?: boolean,
    isLoading?: boolean
  }) => {
    if (!onPageChange) return null
    if (page === 1 && !hasMore) return null

    const totalCount = stageTotalCounts[columnId] || 0
    const totalPages = Math.ceil(totalCount / pageSize)

    return (
      <div className="flex flex-col gap-2 px-3 py-2 border-t mt-auto bg-muted/10 dark:bg-muted/5 rounded-b-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={page <= 1 || isLoading}
              onClick={() => onPageChange(columnId, 1, stepId, status)}
              title={t('common.firstPage', 'Première page')}
            >
              <ChevronsLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={page <= 1 || isLoading}
              onClick={() => onPageChange(columnId, page - 1, stepId, status)}
              title={t('common.previous', 'Précédent')}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
          </div>

          <div className="flex items-center flex-col">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-tight">
              {t('common.page', 'Page')}
            </span>
            <span className="text-xs font-bold text-foreground">
              {page} <span className="text-muted-foreground font-normal text-[10px] lowercase">{t('common.of', 'sur')}</span> {totalPages || 1}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={!hasMore || isLoading}
              onClick={() => onPageChange(columnId, page + 1, stepId, status)}
              title={t('common.next', 'Suivant')}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={page >= totalPages || isLoading}
              onClick={() => onPageChange(columnId, totalPages, stepId, status)}
              title={t('common.lastPage', 'Dernière page')}
            >
              <ChevronsRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">{t('kanban.loadingHiringProcess')}</p>
        </div>
      </div>
    )
  }

  if (filteredColumns.length === 0) {
    return (
      <div className="text-center py-12">
        <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">{t('kanban.noHiringProcessConfigured')}</h3>
        <p className="text-muted-foreground">{t('kanban.configureHiringProcessFirst')}</p>
      </div>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-4">
        {!hideHiringProcessLabel && localizedHiringProcessName && (
          <p className="text-xs text-muted-foreground px-0.5">
            {t('kanban.hiringProcessLabel', 'Hiring process')}:{' '}
            <span className="font-medium text-foreground">{localizedHiringProcessName}</span>
          </p>
        )}
        {/* Kanban Board */}
        <div
          ref={boardScrollRef}
          className="overflow-x-auto custom-scrollbar"
          onScroll={(e) => {
            boardScrollPos.current = (e.target as HTMLDivElement).scrollLeft
          }}
        >
          <div className="flex space-x-6 pb-4" style={{ minWidth: 'max-content' }}>
            {filteredColumns.map((column, index) => {
              const showManageButton = Boolean(onManageCandidates && column.stepType === 'initial')
              const ColumnIcon = column.icon

              // Find custom elements to insert after this column
              const customElementsAfter = customElementsBetweenColumns.filter(
                ce => ce.afterColumnOrder === column.order
              )

              return (
                <React.Fragment key={column.id}>
                  <KanbanColumnContainer
                    column={column}
                    showManageButton={showManageButton}
                    onManageCandidates={onManageCandidates}
                    minimumScore={minimumScore}
                    onMinimumScoreChange={onMinimumScoreChange}
                    minimumInterviewScore={minimumInterviewScore}
                    onMinimumInterviewScoreChange={onMinimumInterviewScoreChange}
                    allCandidates={candidates}
                    hasMatchingRun={hasMatchingRun}
                    stepActions={stepActions}
                    onOpenSendEmailModal={handleOpenSendEmailModal}
                    onDeleteOfferStep={onDeleteOfferStep}
                    isLoadingMore={isLoadingMore}
                    figmaVariant={getKanbanFigmaVariant(column.stepType)}
                    stepColor={column.stepColor}
                    columnCount={stageTotalCounts[column.id] ?? column.candidates.length}
                    initialMatchingMode={filterMatching?.initial_matching_mode}
                    topK={filterMatching?.top_k}
                    onAddCandidates={column.stepType === 'initial' ? onAddCandidates : undefined}
                    onLaunchAIMatching={column.stepType === 'initial' ? onLaunchAIMatching : undefined}
                  >
                    <div
                      ref={(el) => {
                        columnScrollRefs.current[column.id] = el
                      }}
                      onScroll={(e) => {
                        columnScrollPos.current[column.id] = (e.target as HTMLDivElement).scrollTop
                      }}
                      className="flex h-full flex-col"
                    >
                      <SortableContext
                        id={column.id}
                        items={column.candidates.map(c => c.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {column.candidates.length > 0 ? (
                          column.candidates.map((candidate) => (
                            <div key={candidate.id}>
                              <SortableCandidateCard candidate={candidate} containerId={column.id} />
                            </div>
                          ))
                        ) : (
                          <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-8 flex flex-col items-center justify-center h-full">
                            <div className="w-12 h-12 mx-auto mb-3 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                              <ColumnIcon className="h-6 w-6 text-gray-400" />
                            </div>
                            <p>{t('kanban.noCandidates')}</p>
                          </div>
                        )}
                      </SortableContext>

                      {/* Column Pagination Controls */}
                      <ColumnPagination
                        columnId={column.id}
                        stepId={column.stepId}
                        status={stepTypeToApplicationStatus(column.stepType)}
                        page={currentPage[column.id]}
                        hasMore={hasMore[column.id]}
                        isLoading={isLoadingMore[column.id]}
                      />
                    </div>
                  </KanbanColumnContainer>

                  {/* Insert custom elements after this column - absolutely positioned overlay */}
                  {customElementsAfter.map((ce, ceIndex) => (
                    <div
                      key={`custom-${column.order}-${ceIndex}`}
                      className="relative"
                      style={{
                        width: 0,
                        height: '500px',
                        overflow: 'visible',
                        zIndex: 20
                      }}
                    >
                      {ce.element}
                    </div>
                  ))}

                </React.Fragment>
              )
            })}
            {onAddHiringStep && (
              <FigmaKanbanAddStepButton onClick={onAddHiringStep} />
            )}
          </div>
        </div>
      </div>

      {/* Send Email Modal */}
      {showSendEmailModal && selectedStepForEmail && (
        <BulkSendEmailModal
          isOpen={showSendEmailModal}
          onClose={() => {
            setShowSendEmailModal(false)
            setSelectedStepForEmail(null)
            setSelectedCandidatesForEmail([])
          }}
          stepId={selectedStepForEmail.stepId}
          stepName={selectedStepForEmail.stepName}
          stepType={selectedStepForEmail.stepType}
          candidates={selectedCandidatesForEmail}
          stepActions={stepActions[selectedStepForEmail.stepId] || []}
          offerId={offerId}
          hiringSteps={hiringSteps}
        />
      )}

      {/* Drag Overlay */}
      <DragOverlay>
        {activeId ? (
          <div style={{ opacity: 0.5 }}>
            {(() => {
              const draggingCandidate = candidates.find(c => c.id === activeId)
              return draggingCandidate ? <CandidateCard candidate={draggingCandidate} /> : null
            })()}
          </div>
        ) : null}
      </DragOverlay>

      {/* Confirmation Modal for Forward Moves */}
      <Modal
        isOpen={showConfirmModal}
        onClose={handleCancelMove}
        title={t('kanban.confirmMoveTitle', 'Move Candidate Forward')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {pendingMove && t('kanban.confirmMoveMessage', {
              candidate: pendingMove.candidateName,
              from: pendingMove.fromStep,
              to: pendingMove.toStep,
              defaultValue: `Are you sure you want to move ${pendingMove.candidateName} from "${pendingMove.fromStep}" to "${pendingMove.toStep}"?`
            })}
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={handleCancelMove}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="default"
              onClick={handleConfirmMove}
            >
              {t('kanban.confirmMoveCta', 'Confirm Move')}
            </Button>
          </div>
        </div>
      </Modal>
    </DndContext>
  )
}
